import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  trackPageView, 
  trackButtonClick, 
  trackSectionView, 
  trackCustomEvent 
} from '../firebaseConfig';

/**
 * Custom hook for Firebase analytics tracking
 * Automatically tracks page views and provides methods for other tracking events
 */
export const useAnalytics = () => {
  const location = useLocation();
  
  // Track page views automatically
  useEffect(() => {
    // Get readable page name from path
    const getPageName = (path: string): string => {
      // Remove leading slash and split by segments
      const segments = path.replace(/^\/+/, '').split('/');
      
      // If it's the home page, return "Home"
      if (!segments[0]) return 'Home';
      
      // Handle common pages
      switch (segments[0]) {
        case 'login': return 'Login';
        case 'signup': return 'Sign Up';
        case 'dashboard': return 'Dashboard';
        case 'setup-profile': return 'Profile Setup';
        case 'profile-preferences': return 'Profile Preferences';
        case 'interview-preview': 
          return segments[1] ? `Interview Preview: ${segments[1]}` : 'Interview Preview';
        case 'interview':
          return segments[1] ? `Interview Session: ${segments[1]}` : 'Interview Session';
        case 'interview-feedback': return 'Interview Feedback';
        case 'subscription':
        case 'subscriptions': 
          return 'Subscription Plans';
        case 'onboarding':
          return segments[1] ? `Onboarding: ${segments[1]}` : 'Onboarding';
        case 'my-interviews': return 'Interview History';
        case 'interview-series':
          return segments[1] ? `Interview Series: ${segments[1]}` : 'Interview Series';
        case 'settings-preferences': return 'Settings';
        case 'privacy': return 'Privacy Policy';
        case 'terms': return 'Terms of Service';
        default:
          // Capitalize first letter of each segment and join with spaces
          return segments.map(segment => 
            segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
          ).join(' - ');
      }
    };

    const pageName = getPageName(location.pathname);
    trackPageView(pageName);
  }, [location.pathname]);

  // Tracking functions
  const trackButton = useCallback((buttonName: string, buttonLocation: string) => {
    trackButtonClick(buttonName, buttonLocation);
  }, []);

  const trackSection = useCallback((sectionName: string) => {
    trackSectionView(sectionName);
  }, []);

  const trackEvent = useCallback((eventName: string, eventParams: Record<string, any>) => {
    trackCustomEvent(eventName, eventParams);
  }, []);

  return {
    trackButton,
    trackSection,
    trackEvent
  };
};

export default useAnalytics; 